import { Card, CircularProgress, Stack } from "@mui/material";
import { deleteEmailTemplate, getEmailTemplates } from "backend/api/emailing";
import { DeleteEmailTemplateDialog } from "components/emailing/EmailTemplate/DeleteEmailTemplateDialog/DeleteEmailTemplateDialog";
import { EmailTemplatesList } from "components/emailing/EmailTemplate/EmailTemplatesList/EmailTemplatesList";
import { toastEmitter } from "helpers/helpers";
import { useError } from "hooks/useError";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { EmailTemplate } from "types/emailTemplate.type";
import "./EmailTemplatesPage.css";

export function EmailTemplatesPage() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);

  const { t } = useTranslation("emailing");
  const { id } = useParams();

  const navigate = useNavigate();
  const { onError } = useError(t, "errors.fetchError");

  const { isLoading, isFetched } = useQuery({
    queryKey: ["email-templates"],
    queryFn: () => getEmailTemplates(),
    onSuccess: (data) => {
      if (data?.data) {
        setTemplates(data.data);
      }
    },
    onError,
  });

  const { isLoading: isDeleting, mutate: deleteTemplate } = useMutation({
    mutationFn: (templateId: number) => deleteEmailTemplate(templateId),
    onSuccess: (data) => {
      setTemplates((prev) =>
        prev.filter((template) => template.id !== data.data.id),
      );
      setSelectedTemplate(null);
      navigate("add", { replace: true });
      toastEmitter(t("success.templateDeleted"), "success");
    },
    onError,
  });

  const handleSelectTemplate = (template: EmailTemplate) => {
    navigate(`${template.id}`);
  };

  const handleAddTemplate = () => {
    navigate("add");
  };

  const handleConfirmDeleteTemplate = (template: EmailTemplate) => {
    deleteTemplate(template.id);
  };

  const handleOpenDeleteTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
  };

  const handleCloseDeleteTemplate = () => {
    setSelectedTemplate(null);
  };

  return (
    <Stack direction="column" spacing={1} width="100%">
      <DeleteEmailTemplateDialog
        template={selectedTemplate}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDeleteTemplate}
        onClose={handleCloseDeleteTemplate}
      />
      <Card>
        {isLoading && (
          <Stack alignItems={"center"}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {isFetched && (
          <Stack className="list-content">
            <EmailTemplatesList
              templates={templates}
              selectedTemplateId={Number(id)}
              isDeleting={isDeleting}
              onClickAdd={handleAddTemplate}
              onClick={handleSelectTemplate}
              onDelete={handleOpenDeleteTemplate}
            />
          </Stack>
        )}
      </Card>
      {isFetched && <Outlet />}
    </Stack>
  );
}
